// ---------------------------------------------------------------
// useToast.js — Avisos flotantes, en un único sitio.
//
// Antes cada pantalla tenía su propio `const [msg, setMsg]` y pintaba
// un `<div className="admin-toast">` donde le pillaba: unos empujaban
// el contenido hacia abajo al aparecer, otros salían dentro de una
// tarjeta y otros no se iban nunca. Eran 46 repartidos por 14
// ficheros, cada uno con su comportamiento.
//
// Ahora se pide un aviso desde cualquier sitio:
//
//   const toast = useToast();
//   toast.ok("Premisas guardadas");
//   toast.error("No se pudo guardar");
//
// y aparece siempre en la misma esquina, sin mover nada de sitio y
// desapareciendo solo.
// ---------------------------------------------------------------
import { createContext, useCallback, useContext, useMemo, useRef, useState } from "react";

const Ctx = createContext(null);

// Cuánto dura cada tipo. Los errores aguantan más porque suelen
// llevar una instrucción que hay que leer entera.
const DURACION = { ok: 4000, error: 7000, info: 5000 };

export function ToastProvider({ children }) {
  const [avisos, setAvisos] = useState([]);
  const siguienteId = useRef(1);

  const quitar = useCallback((id) => {
    setAvisos((prev) => prev.filter((a) => a.id !== id));
  }, []);

  const mostrar = useCallback(
    (tipo, texto, opciones = {}) => {
      if (!texto) return null;
      const id = siguienteId.current++;
      const ms = opciones.duracion ?? DURACION[tipo] ?? 5000;
      setAvisos((prev) => {
        // Si se repite el mismo mensaje, se refresca en vez de apilar
        const sinRepetido = prev.filter((a) => a.texto !== texto);
        return [...sinRepetido, { id, tipo, texto, ms }].slice(-3);
      });
      if (ms > 0) setTimeout(() => quitar(id), ms);
      return id;
    },
    [quitar]
  );

  const api = useMemo(
    () => ({
      ok: (texto, o) => mostrar("ok", texto, o),
      error: (texto, o) => mostrar("error", texto, o),
      info: (texto, o) => mostrar("info", texto, o),
      quitar,
      avisos,
    }),
    [mostrar, quitar, avisos]
  );

  return <Ctx.Provider value={api}>{children}</Ctx.Provider>;
}

/**
 * Si alguien lo usa fuera del proveedor (un test, por ejemplo) se
 * devuelve una versión que no hace nada, en vez de reventar: un aviso
 * que no sale nunca debe ser preferible a una pantalla en blanco.
 */
const VACIO = { ok: () => {}, error: () => {}, info: () => {}, quitar: () => {}, avisos: [] };

export function useToast() {
  return useContext(Ctx) || VACIO;
}
